import React from 'react';
import { motion } from 'framer-motion';

/*
 * Logos: Using processed versions for dark-background compatibility
 * - Processed logos: white bg removed, dark text → white, colors preserved
 * - External logos (Maxton, APR): already work on dark backgrounds
 */
const LOGOS = [
  { name: 'Maxton Design', logo: '/images/brands/maxton-design.png', url: '/marke/maxton-design', featured: true },
  { name: 'APR', logo: 'https://goapr.de/bundles/rjscratchtheme/assets/images/thmgraphics/logo_apr.svg', url: '/marke/apr', featured: true },
  { name: 'Grail', logo: '/images/brands/grail.png', url: '/marke/grail-automotive-gmbh', featured: true },
  { name: 'Remus', logo: '/images/brands/remus.png', url: '/marke/remus', featured: true },
  { name: 'Akrapovic', logo: '/images/brands/akrapovic-processed.png', url: '/marke/akrapovi-d-d', featured: true },
  { name: 'Eventuri', logo: '/images/brands/eventuri-processed.png', url: '/marke/eventuri' },
  { name: 'Hurricane', logo: '/images/brands/hurricane-processed.png', url: '/marke/hurricane-exhaust', featured: true, wide: true },
  { name: 'HG-Motorsport', logo: '/images/brands/hg-motorsport-processed.png', url: '/marke/hg-motorsport-gmbh' },
  { name: 'E-Intake', logo: '/images/brands/e-intake.png', url: '/marke/e-exhaust-e-intake' },
  { name: 'Novitec', logo: '/images/brands/novitec-processed.png', url: '/marke/novitec' },
];

const FEATURED = LOGOS.filter(l => l.featured);

const CAR_BRANDS = [
  'Audi', 'BMW', 'Mercedes-Benz', 'Volkswagen', 'Porsche',
  'Ford', 'Seat / Cupra', 'Skoda', 'Mazda', 'Toyota',
  'Opel', 'Mini', 'Alfa Romeo', 'Kia', 'Hyundai',
];

const BrandMarquee = () => (
  <section id="brands" className="section" data-testid="brands-section" style={{ overflow: 'hidden' }}>
    <div style={{ maxWidth: "1400px", margin: "0 auto", padding: "0 1.5rem", marginBottom: "3rem" }}>
      <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
        style={{ textAlign: 'center' }}>
        <div className="caption" style={{ marginBottom: '1rem' }}>Unsere Partner</div>
        <h2 className="heading-section" style={{ fontSize: 'clamp(1.5rem, 4vw, 2.5rem)' }}>
          Premium-<span style={{ color: 'var(--primary)' }}>Marken</span>
        </h2>
      </motion.div>
    </div>

    {/* Featured partners */}
    <div style={{ maxWidth: "1400px", margin: "0 auto", padding: "0 1.5rem", marginBottom: "2.5rem" }}>
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap', gap: '0' }}>
        {FEATURED.map((brand, i) => (
          <motion.a key={brand.name} href={brand.url} target="_blank" rel="noopener noreferrer"
            data-testid={`partner-${brand.name.toLowerCase().replace(/[\s-]/g, '-')}`}
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ delay: i * 0.05 }}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: '2rem 1.5rem', textDecoration: 'none',
              border: '1px solid rgba(255,255,255,0.05)',
              flex: '1 1 180px', minHeight: '100px', maxWidth: '280px', transition: 'all 0.4s',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(255,46,46,0.3)'; e.currentTarget.style.background = 'rgba(255,46,46,0.04)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.05)'; e.currentTarget.style.background = 'transparent'; }}
          >
            <img src={brand.logo} alt={brand.name}
              style={{ height: brand.wide ? '48px' : '50px', maxWidth: brand.wide ? '240px' : '180px', objectFit: 'contain', transition: 'transform 0.3s' }}
              onMouseEnter={e => e.target.style.transform = 'scale(1.08)'}
              onMouseLeave={e => e.target.style.transform = 'scale(1)'}
            />
          </motion.a>
        ))}
      </div>
    </div>

    {/* Scrolling marquee */}
    <div style={{ overflow: 'hidden', borderTop: '1px solid rgba(255,255,255,0.06)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}
      onMouseEnter={e => { const t = e.currentTarget.querySelector('.marquee-track'); if(t) t.style.animationPlayState = 'paused'; }}
      onMouseLeave={e => { const t = e.currentTarget.querySelector('.marquee-track'); if(t) t.style.animationPlayState = 'running'; }}
    >
      <div className="marquee-track" style={{ gap: '0px', animationDuration: '50s' }}>
        {[...LOGOS, ...LOGOS, ...LOGOS, ...LOGOS].map((brand, i) => (
          <a key={i} href={brand.url} target="_blank" rel="noopener noreferrer"
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: '1.5rem 1.5rem', textDecoration: 'none',
              borderRight: '1px solid rgba(255,255,255,0.04)',
              transition: 'background 0.3s', minWidth: brand.wide ? '230px' : '180px',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,46,46,0.05)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
          >
            <img src={brand.logo} alt={brand.name}
              style={{ height: brand.wide ? '34px' : '32px', maxWidth: brand.wide ? '200px' : '140px', objectFit: 'contain', opacity: 0.7, transition: 'opacity 0.3s' }}
              onMouseEnter={e => e.target.style.opacity = '1'}
              onMouseLeave={e => e.target.style.opacity = '0.7'}
              onError={e => { e.target.style.display = 'none'; }}
            />
          </a>
        ))}
      </div>
    </div>

    {/* Car brands tags */}
    <div style={{ maxWidth: "1400px", margin: "0 auto", padding: "0 1.5rem", paddingTop: "3rem" }}>
      <p style={{ textAlign: 'center', fontSize: '0.7rem', color: 'var(--fg-muted)', textTransform: 'uppercase', letterSpacing: '0.2em', marginBottom: '1.25rem', fontWeight: 600 }}>
        Teile für dein Fahrzeug
      </p>
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '0.5rem' }}>
        {CAR_BRANDS.map(brand => (
          <a key={brand} href={`/shop?q=${encodeURIComponent(brand)}`}
            style={{
              padding: '0.5rem 1.25rem', border: '1px solid rgba(255,255,255,0.08)',
              fontFamily: 'var(--font-heading)', fontWeight: 600, fontSize: '0.8rem',
              textTransform: 'uppercase', letterSpacing: '0.05em', color: 'rgba(255,255,255,0.4)',
              textDecoration: 'none', transition: 'all 0.3s',
            }}
            onMouseEnter={e => { e.target.style.borderColor = 'var(--primary)'; e.target.style.color = 'white'; e.target.style.background = 'rgba(255,46,46,0.1)'; }}
            onMouseLeave={e => { e.target.style.borderColor = 'rgba(255,255,255,0.08)'; e.target.style.color = 'rgba(255,255,255,0.4)'; e.target.style.background = 'transparent'; }}
          >{brand}</a>
        ))}
      </div>
    </div>
  </section>
);

export default BrandMarquee;
